import { ConnectError, createPromiseClient, type Interceptor } from '@connectrpc/connect'
import { createGrpcWebTransport } from '@connectrpc/connect-web'
import {
  isClientIdFailure,
  isRetriableAuthFailure,
  toGrpcFailure,
} from '~/utils/grpcError'

type SsrTimingContext = {
  _startTimer?: number
  _apiTimings?: { label: string, dur: number, offset: number }[]
}

function createRefreshManager(userStore: ReturnType<typeof useUserStore>) {
  let refreshPromise: Promise<string | null> | null = null
  let accessToken: string | null = null

  async function runRefreshFlow() {
    if (refreshPromise) return refreshPromise

    refreshPromise = (async () => {
      try {
        const success = await userStore.refresh()
        if (success) {
          accessToken = success.accessToken
        }
        return success ? success.accessToken : null
      } catch (e) {
        useConsole().info('runRefreshFlow', 'error')
        return null
      } finally {
        if (import.meta.client) {
          refreshPromise = null
        }
      }
    })()

    return refreshPromise
  }

  return {
    runRefreshFlow,
    getAccessToken: () => accessToken,
  }
}

export default defineNuxtPlugin({
  name: 'app:grpc-instance',

  setup() {
    const userStore = useUserStore()
    const deviceStore = useDeviceStore()
    const config = useRuntimeConfig()

    const timingEnabled = import.meta.server && config.IS_DEBUG_SSR_TIMING
    const timingEvent = import.meta.server ? useRequestEvent() : null

    const refreshManager = createRefreshManager(userStore)
    const refreshedRequests = new WeakMap<any, boolean>()

    let clientsPromise: ReturnType<typeof buildClients> | null = null

    async function buildClients() {
      const Services = await import('~/grpc/generated/content_connect')

      const authInterceptor: Interceptor = next => async (req) => {
        const token = refreshManager.getAccessToken() || userStore.accessToken
        if (token) {
          req.header.set('Authorization', `Bearer ${token}`)
        }
        return await next(req)
      }

      const retryInterceptor: Interceptor = next => async (req) => {
        try {
          return await next(req)
        } catch (error) {
          const connectError = ConnectError.from(error)
          const failure = toGrpcFailure(connectError)

          if (isClientIdFailure(failure)) {
            await deviceStore.getDeviceClientId()
            return await next(req)
          }

          if (isRetriableAuthFailure(failure)) {
            const isAuthEntrypoint = req.service === Services.AuthService
              && ['Login', 'Revoke', 'Logout'].includes(req.method.name)

            if (isAuthEntrypoint) {
              throw error
            }

            if (!refreshedRequests.has(req)) {
              refreshedRequests.set(req, true)
              const newToken = await refreshManager.runRefreshFlow()

              if (newToken) {
                req.header.set('Authorization', `Bearer ${newToken}`)
                return await next(req)
              }
            }

            throw error
          }

          throw error
        }
      }

      const timingInterceptor: Interceptor = next => async (req) => {
        if (!timingEnabled || !timingEvent) {
          return await next(req)
        }
        const start = performance.now()
        try {
          return await next(req)
        } finally {
          const context = timingEvent.context as SsrTimingContext
          context._apiTimings ??= []
          context._apiTimings.push({
            label: `${req.service.typeName}/${req.method.name}`,
            dur: performance.now() - start,
            offset: context._startTimer ? start - context._startTimer : 0,
          })
        }
      }

      const baseUrl = import.meta.server
        ? (config.GRPC_INTERNAL_ENDPOINT || config.public.GRPC_ENDPOINT)
        : config.public.GRPC_ENDPOINT

      const transport = createGrpcWebTransport({
        baseUrl,
        useBinaryFormat: true,
        interceptors: [timingInterceptor, retryInterceptor, authInterceptor],
      })

      return {
        auth: createPromiseClient(Services.AuthService, transport),
        device: createPromiseClient(Services.DeviceService, transport),
        status: createPromiseClient(Services.StatusService, transport),
        movie: createPromiseClient(Services.MovieService, transport),
        genre: createPromiseClient(Services.GenreService, transport),
        studio: createPromiseClient(Services.StudioService, transport),
        staff: createPromiseClient(Services.StaffService, transport),
        search: createPromiseClient(Services.SearchService, transport),
        playlist: createPromiseClient(Services.PlaylistService, transport),
        userPlaylist: createPromiseClient(Services.UserPlaylistService, transport),
        statistics: createPromiseClient(Services.StatisticsService, transport),
        sitemap: createPromiseClient(Services.SitemapService, transport),
        analytics: createPromiseClient(Services.AnalyticsService, transport),
        telegramMailing: createPromiseClient(Services.TelegramMailingService, transport),
        recommendation: createPromiseClient(Services.RecommendationService, transport),
        catalog: createPromiseClient(Services.CatalogService, transport),
        botAnalytics: createPromiseClient(Services.BotAnalyticsService, transport),
        media: createPromiseClient(Services.MediaService, transport),
        banner: createPromiseClient(Services.BannerService, transport),
        adVast: createPromiseClient(Services.AdVastService, transport),
        billing: createPromiseClient(Services.BillingService, transport),
        collection: createPromiseClient(Services.CollectionService, transport),
        maintenance: createPromiseClient(Services.MaintenanceService, transport),
        notification: createPromiseClient(Services.NotificationService, transport),
        systemNotification: createPromiseClient(Services.SystemNotificationService, transport),
        contactRequest: createPromiseClient(Services.ContactRequestService, transport),
        marketing: createPromiseClient(Services.MarketingService, transport),
      }
    }

    function getGrpcClients() {
      if (!clientsPromise) {
        clientsPromise = buildClients()
      }
      return clientsPromise
    }

    return {
      provide: {
        getGrpcClients,
        grpcRefreshToken: refreshManager.runRefreshFlow,
      },
    }
  },
})
